import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useRef, useState } from 'react';
import { Button, CircularLoader, TextButton, Typography } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import OTPVerificationField from '~/components/OTPVerificationField/OTPVerificationField';
import { routes } from '~/constants';
import { useEventReporter, useIoContext, useStoreActions } from '~/contexts';
import { useOnMount } from '~/hooks/useOnMount';
import { useUtmsParams } from '~/hooks/useUtmsParams';
import { useLocaleKeys } from '~/i18n';
import { handlePromise } from '~/infra/utils';
import { LoginMethod, useLoginTelemetry } from '~/loginTelemetry/useLoginTelemetry';
import { usePostAuthRedirect } from '~/utils/auth-redirect';
import { ErrorMessage, FormContainer, FormFields, VerticalFormFields } from '../../components/FormComponents';
import { FormSeparatorHorizontal } from '../../components/FormSeparator';
import { LoginFlow } from '../LoginScreen.types';
import { getOtpErrorCause } from './OTPField.utils';
import styles from './OTPForm.module.scss';
export const GetOTPFormTID = (parentTestId)=>getTestIds({
        parentTestId,
        deprecatedTestIds: {
            OTPFormTID: {
                Form: 'otp-form',
                Title: 'otp-form-title',
                Loading: 'loading-container',
                Error: 'error-message',
                InfoMessage: 'info-message',
                GetNewCode: 'get-new-code-button',
                Back: 'back-link'
            }
        }
    });
export default function OTPForm(param) {
    let { email, flow, onBack, shouldRequestOTP } = param;
    const { t: otpTexts } = useLocaleKeys('auth.screens.otp');
    const { reportFormSubmission, reportToHeap } = useEventReporter();
    const { browser: { changeLocation } } = useIoContext();
    const performPostAuthRedirect = usePostAuthRedirect();
    const { api, logBI } = useStoreActions();
    const getUtms = useUtmsParams();
    const [otpSessionStatus, setOtpSessionStatus] = useState();
    const [hasError, setHasError] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const isValidatingRef = useRef(false);
    const otpFieldRef = useRef(null);
    const { loginSucceeded, loginFailed } = useLoginTelemetry();
    const { OTPFormTID } = useMemo(()=>GetOTPFormTID(), []);
    const notices = {
        otpRequestOK: {
            text: otpTexts.newCodeSent(),
            displaysInline: false,
            isError: false
        },
        tooManyRequests: {
            text: otpTexts.tooManyRequests(),
            displaysInline: false,
            isError: true
        },
        emailAlreadyVerified: {
            text: otpTexts.emailAlreadyVerified(),
            displaysInline: false,
            isError: true
        },
        wrongCode: {
            text: otpTexts.wrongCode(),
            displaysInline: true,
            isError: true
        },
        unknown: {
            text: otpTexts.unknownError(),
            displaysInline: false,
            isError: true
        }
    };
    const notice = notices[otpSessionStatus !== null && otpSessionStatus !== void 0 ? otpSessionStatus : ''];
    const inlineError = (notice === null || notice === void 0 ? void 0 : notice.isError) && (notice === null || notice === void 0 ? void 0 : notice.displaysInline) && (notice === null || notice === void 0 ? void 0 : notice.text);
    const floatingError = (notice === null || notice === void 0 ? void 0 : notice.isError) && !(notice === null || notice === void 0 ? void 0 : notice.displaysInline) && (notice === null || notice === void 0 ? void 0 : notice.text);
    const floatingMessage = !(notice === null || notice === void 0 ? void 0 : notice.isError) && (notice === null || notice === void 0 ? void 0 : notice.text);
    useOnMount(()=>{
        async function requestOTPOnMount() {
            if (shouldRequestOTP) {
                const result = flow === LoginFlow.EmailVerification ? await handlePromise(api.auth.requestNewOTPForSignupFlow(email)) : await handlePromise(api.auth.requestNewOTP(email));
                if (!result.success) {
                    setOtpSessionStatus(getOtpErrorCause(result.error));
                }
            }
        }
        requestOTPOnMount();
    });
    async function validateOTP(otp) {
        if (isValidatingRef.current) {
            return;
        }
        isValidatingRef.current = true;
        setIsLoading(true);
        try {
            const result = flow === LoginFlow.EmailVerification ? await handlePromise(api.auth.validateOTPForSignupFlow(email, otp, getUtms())) : await handlePromise(api.auth.validateOTP(email, otp, getUtms()));
            if (!result.success) {
                setOtpSessionStatus(getOtpErrorCause(result.error));
                setHasError(true);
                setIsLoading(false);
                loginFailed(LoginMethod.OTP);
            } else {
                reportFormSubmission('general-logged-in-successfully', null);
                reportToHeap('Login Screen', 'click', 'signed-in-with-six-digits-code', {
                    email
                });
                logBI(16001, {
                    email
                });
                loginSucceeded(LoginMethod.OTP);
                if (flow === LoginFlow.EmailVerification) {
                    changeLocation(routes.home);
                } else {
                    performPostAuthRedirect();
                }
            }
        } finally{
            isValidatingRef.current = false;
        }
    }
    async function requestNewOTP() {
        logBI(16000, {
            email
        });
        reportToHeap('Login Screen', 'click', 'clicked-send-me-six-digits-code', {
            email
        });
        const result = flow === LoginFlow.EmailVerification ? await handlePromise(api.auth.requestNewOTPForSignupFlow(email)) : await handlePromise(api.auth.requestNewOTP(email));
        if (!result.success) {
            setOtpSessionStatus(getOtpErrorCause(result.error));
        } else {
            setOtpSessionStatus('otpRequestOK');
        }
    }
    return /*#__PURE__*/ _jsxs(FormContainer, {
        onSubmit: (e)=>e.preventDefault(),
        "data-testid": OTPFormTID.Form(),
        className: styles.otpForm,
        children: [
            /*#__PURE__*/ _jsx(Typography, {
                testId: OTPFormTID.Title(),
                variant: "h5",
                weight: "normal",
                className: styles.title,
                children: flow === LoginFlow.EmailVerification ? otpTexts.verifyYourEmail() : otpTexts.checkYourEmail()
            }),
            /*#__PURE__*/ _jsx(FormFields, {
                className: styles.formFields,
                children: /*#__PURE__*/ _jsxs(VerticalFormFields, {
                    className: styles.verticalFormFields,
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            className: styles.subtitle,
                            children: /*#__PURE__*/ _jsxs(Typography, {
                                children: [
                                    otpTexts.enterCodeSentTo(),
                                    ' ',
                                    /*#__PURE__*/ _jsx(Typography, {
                                        as: "span",
                                        weight: "bold",
                                        children: email
                                    })
                                ]
                            })
                        }),
                        /*#__PURE__*/ _jsx(OTPVerificationField, {
                            ref: otpFieldRef,
                            onChange: validateOTP,
                            hasError: hasError,
                            onInputChange: ()=>{
                                if (notice === null || notice === void 0 ? void 0 : notice.isError) {
                                    setOtpSessionStatus(undefined);
                                }
                            }
                        }),
                        isLoading && /*#__PURE__*/ _jsx("div", {
                            className: styles.loadingContainer,
                            "data-testid": OTPFormTID.Loading(),
                            children: /*#__PURE__*/ _jsx(CircularLoader, {
                                size: 24,
                                centeredInBox: true
                            })
                        }),
                        inlineError && /*#__PURE__*/ _jsx(ErrorMessage, {
                            testId: OTPFormTID.Error(),
                            children: inlineError
                        }),
                        floatingMessage && /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph2",
                            testId: OTPFormTID.InfoMessage(),
                            className: styles.floatingMessage,
                            children: floatingMessage
                        }),
                        floatingError && /*#__PURE__*/ _jsx(ErrorMessage, {
                            testId: OTPFormTID.Error(),
                            children: floatingError
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: styles.footer,
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: styles.newCodeContent,
                        children: [
                            /*#__PURE__*/ _jsx(FormSeparatorHorizontal, {
                                className: styles.separator,
                                children: otpTexts.couldntFindInInbox()
                            }),
                            /*#__PURE__*/ _jsx(Button, {
                                bi: {
                                    name: 'request_new_otp_code'
                                },
                                skin: "primaryOutline",
                                testId: OTPFormTID.GetNewCode(),
                                size: "medium",
                                className: styles.getNewCodeButton,
                                onClick: async (e)=>{
                                    var _otpFieldRef_current;
                                    e.preventDefault();
                                    requestNewOTP();
                                    (_otpFieldRef_current = otpFieldRef.current) === null || _otpFieldRef_current === void 0 ? void 0 : _otpFieldRef_current.reset();
                                },
                                children: otpTexts.getNewCode()
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(TextButton, {
                        bi: {
                            name: 'back_to_login_email'
                        },
                        underline: "none",
                        skin: "secondary",
                        onClick: onBack,
                        testId: OTPFormTID.Back(),
                        className: styles.backButton,
                        children: otpTexts.back()
                    })
                ]
            })
        ]
    });
}
